import { create } from 'zustand';

/**
 * Who currently displays the batch. While the modal owns it, the epilogue
 * (`bulkProgress`) shows the detailed recap and the global toast stays silent;
 * closing the modal mid-flight transfers ownership so the toast takes over the
 * aggregate progress.
 */
export type BulkInviteOwner = 'modal' | 'toast';

/** Live per-recipient email delivery, keyed by invitation id. Email mode only:
 * the send jobs run AFTER the batch is queued and emit outreach events that
 * flip a recap line from "sending" to "sent"/"failed". */
export interface BulkInviteDelivery {
  status: 'sent' | 'failed' | 'unknown';
  /** English failure text, the recap's fallback when the code has no translation */
  message?: string | null;
  /** Failure reason as a translatable code; the recap prefers it over `message` */
  errorCode?: string | null;
}

interface BulkInviteBatch {
  batchId: string;
  total: number;
  sent: number;
  failed: number;
  status: 'processing' | 'completed' | 'error';
  owner: BulkInviteOwner;
  onComplete?: () => void;
}

interface StartBatchOptions {
  owner?: BulkInviteOwner;
  onComplete?: () => void;
}

interface BulkInviteProgressStore {
  activeBatch: BulkInviteBatch | null;
  /** Live email delivery outcomes, keyed by invitation id (email mode). */
  deliveryByInvitationId: Record<string, BulkInviteDelivery>;

  // Actions
  startBatch: (batchId: string, total: number, options?: StartBatchOptions) => void;
  updateProgress: (sent: number, failed: number) => void;
  completeBatch: (sent: number, failed: number) => void;
  /** Record a per-recipient email send outcome (from the outreach send job). */
  recordDelivery: (invitationId: string, delivery: BulkInviteDelivery) => void;
  /** Hand display over to the global toast (modal closed while processing). */
  transferToToast: () => void;
  clearBatch: () => void;
}

const useBulkInviteProgressStore = create<BulkInviteProgressStore>((set, get) => ({
  activeBatch: null,
  deliveryByInvitationId: {},

  startBatch: (batchId, total, options) => {
    set({
      activeBatch: {
        batchId,
        total,
        sent: 0,
        failed: 0,
        status: 'processing',
        // The modal owns a batch by default (it starts every send); a rare
        // toast-only path can opt out explicitly.
        owner: options?.owner ?? 'modal',
        onComplete: options?.onComplete,
      },
      // A fresh batch starts with no per-recipient outcomes.
      deliveryByInvitationId: {},
    });
  },

  updateProgress: (sent, failed) => {
    const { activeBatch } = get();
    if (!activeBatch) return;

    set({
      activeBatch: {
        ...activeBatch,
        sent,
        failed,
      },
    });
  },

  completeBatch: (sent, failed) => {
    const { activeBatch } = get();
    if (!activeBatch) return;

    // Store callback reference before state update
    const onCompleteCallback = activeBatch.onComplete;

    // Update state first so callback sees completed status
    set({
      activeBatch: {
        ...activeBatch,
        sent,
        failed,
        status: 'completed',
      },
    });

    // Then call callback after state is updated
    onCompleteCallback?.();
  },

  recordDelivery: (invitationId, delivery) => {
    if (!invitationId) return;
    set((state) => ({
      deliveryByInvitationId: { ...state.deliveryByInvitationId, [invitationId]: delivery },
    }));
  },

  transferToToast: () => {
    const { activeBatch } = get();
    if (!activeBatch || activeBatch.owner === 'toast') return;
    set({ activeBatch: { ...activeBatch, owner: 'toast' } });
  },

  clearBatch: () => {
    set({ activeBatch: null, deliveryByInvitationId: {} });
  },
}));

export default useBulkInviteProgressStore;
